import { CONTACT_EMAIL, LegalPage, MailTo, OPERATOR, Section } from "./LegalPage";

/** Terms of Service. Standard boilerplate — see the note in Contact.tsx about the
 *  placeholders that need real values before this is relied on. */
export function Terms() {
  return (
    <LegalPage title="Terms of Service" updated="November 3, 2026">
      <p>
        These Terms govern your use of goonlords (the &ldquo;Service&rdquo;,
        &ldquo;we&rdquo;, &ldquo;us&rdquo;). By accessing or using the Service you agree to
        them. If you do not agree, do not use the Service.
      </p>

      <Section title="1. Age requirement">
        The Service contains sexually explicit material intended solely for adults. You
        must be at least 18 years old, or the age of majority in your jurisdiction if
        higher, to access it. By using the Service you represent and warrant that you meet
        this requirement and that it is lawful for you to view such material where you are.
      </Section>

      <Section title="2. Accounts">
        <ul className="list-disc space-y-1 pl-5">
          <li>You are responsible for activity under your account and for keeping your
            credentials secure.</li>
          <li>You may not share, sell, or transfer an account.</li>
          <li>We may suspend or terminate an account that violates these Terms.</li>
          <li>You may delete your account at any time.</li>
          <li>You can buy from the shop without an account. A guest order is reached through its
            private link, or its order number and the email used at checkout; keep the link to
            yourself.</li>
        </ul>
      </Section>

      <Section title="3. Acceptable use">
        You agree not to:
        <ul className="mt-1 list-disc space-y-1 pl-5">
          <li>Upload, submit, or link to any material depicting a minor, or that you do not
            have the right to submit.</li>
          <li>Upload material depicting any person who has not consented to its creation
            and distribution, including non-consensual or intimate imagery.</li>
          <li>Use the Service for any unlawful purpose, or in violation of any applicable
            law or regulation.</li>
          <li>Scrape, crawl, resell, or systematically download the Service or its content
            except as expressly permitted.</li>
          <li>Interfere with, overload, or attempt to gain unauthorised access to the
            Service, its infrastructure, or another user&rsquo;s account.</li>
          <li>Circumvent any age gate, access control, rate limit, or security measure.</li>
        </ul>
      </Section>

      <Section title="4. Content on the Service">
        The Service organises and displays material, some of which originates from third
        parties and is not created by {OPERATOR}. We do not endorse third-party material and
        make no representation about it. If you believe material infringes your copyright,
        see our <a href="/dmca" className="text-[var(--color-accent)] hover:underline">DMCA
        policy</a>. If you believe material depicts a minor or a non-consenting person,
        report it immediately to <MailTo subject="Urgent content report" /> and we will act
        on it as a priority.
      </Section>

      <Section title="5. Your content">
        You retain ownership of anything you submit. By submitting it you grant {OPERATOR} a
        non-exclusive, worldwide, royalty-free licence to host, store, reproduce, and display
        it for the purpose of operating and improving the Service. You represent that you
        hold all rights necessary to grant that licence, including any required consents and
        records for depicted persons. The licence ends when you delete the content, except
        for copies retained in backups for a reasonable period.
      </Section>

      <Section title="6. Our content">
        The Service&rsquo;s software, design, branding, and original material are owned by{" "}
        {OPERATOR} and protected by intellectual property law. These Terms grant you a
        limited, personal, revocable, non-transferable licence to use the Service; they
        grant no other rights.
      </Section>

      <Section title="7. Disclaimers">
        THE SERVICE IS PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE&rdquo;, WITHOUT
        WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY,
        FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. We do not warrant
        that the Service will be uninterrupted, secure, or error-free, or that any content
        is accurate or lawful in your jurisdiction.
      </Section>

      <Section title="8. Limitation of liability">
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, {OPERATOR.toUpperCase()} WILL NOT BE LIABLE
        FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR EXEMPLARY DAMAGES, OR FOR
        ANY LOSS OF PROFITS, DATA, OR GOODWILL, ARISING OUT OF OR RELATING TO YOUR USE OF
        THE SERVICE. OUR TOTAL AGGREGATE LIABILITY FOR ANY CLAIM RELATING TO THE SERVICE
        WILL NOT EXCEED THE GREATER OF THE AMOUNT YOU PAID US IN THE TWELVE MONTHS BEFORE
        THE CLAIM, OR ONE HUNDRED U.S. DOLLARS ($100).
      </Section>

      <Section title="9. Indemnification">
        You agree to indemnify and hold harmless {OPERATOR} and those who operate it from any
        claim, demand, loss, or expense (including reasonable legal fees)
        arising out of your use of the Service, your content, or your breach of these Terms.
      </Section>

      <Section title="10. Termination">
        We may suspend or terminate your access at any time, with or without notice, for
        conduct we reasonably believe violates these Terms or exposes us or others to
        liability. Sections 5 through 9 survive termination.
      </Section>

      <Section title="11. Changes">
        We may revise these Terms. Material changes will be reflected in the &ldquo;Last
        updated&rdquo; date above, and continued use after a change constitutes acceptance.
      </Section>

      <Section title="12. Governing law">
        These Terms are governed by the laws of the Commonwealth of Massachusetts, without
        regard to its conflict-of-laws rules. Any dispute arising out of or relating to
        these Terms or the Service will be brought in the state or federal courts located
        in Massachusetts, and you consent to their jurisdiction.
      </Section>

      <Section title="13. Contact">
        Questions about these Terms: <MailTo subject="Terms of Service" />. A full
        list of contact routes is on the{" "}
        <a href="/contact" className="text-[var(--color-accent)] hover:underline">
          contact page
        </a>
        , and every route reaches {CONTACT_EMAIL}.
      </Section>
    </LegalPage>
  );
}
